import { useUpdateTodo, useDeleteTodo } from "@/tasks/hooks";

import type { Todo } from "@/tasks/types";

interface TaskCardProps {
  task: Todo;
}

export const TaskCard = ({ task }: TaskCardProps) => {
  const updateMutation = useUpdateTodo();
  const deleteMutation = useDeleteTodo();

  const handleToggleComplete = () => {
    updateMutation.mutate({
      id: task.id,
      completed: !task.completed,
    });
  };

  const handleDelete = () => {
    if (confirm("Tem certeza que deseja deletar essa tarefa?")) {
      deleteMutation.mutate(task.id);
    }
  };

  const handleEditTitle = (newTitle: string) => {
    if (newTitle.trim() && newTitle !== task.title) {
      updateMutation.mutate({
        id: task.id,
        title: newTitle.trim(),
      });
    }
  };

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-[16px] border border-white/20 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 flex-1">
          <button
            onClick={handleToggleComplete}
            disabled={updateMutation.isPending}
            className={`
              w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all
              ${
                task.completed
                  ? "bg-green-500 border-green-500 text-white"
                  : "border-gray-300 hover:border-green-400"
              }
              ${updateMutation.isPending ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
            `}
          >
            {task.completed && (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>

          <div className="flex-1">
            <input
              type="text"
              defaultValue={task.title}
              onBlur={(e) => handleEditTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleEditTitle(e.currentTarget.value);
                  e.currentTarget.blur();
                }
              }}
              className={`
                w-full bg-transparent border-none outline-none text-gray-900 text-lg font-medium
                ${task.completed ? "line-through text-gray-500" : ""}
                ${updateMutation.isPending ? "opacity-50" : ""}
                focus:bg-white focus:px-2 focus:py-1 focus:rounded transition-all
              `}
              disabled={updateMutation.isPending}
            />
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {(updateMutation.isPending || deleteMutation.isPending) && (
            <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          )}

          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className={`
              p-2 rounded-lg transition-colors
              ${
                deleteMutation.isPending
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-red-50 text-red-500 hover:text-red-700"
              }
            `}
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"
                clipRule="evenodd"
              />
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>

      {(updateMutation.isPending || deleteMutation.isPending) && (
        <div className="mt-3 text-sm text-gray-500 flex items-center">
          <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
          {updateMutation.isPending && "Atualizando..."}
          {deleteMutation.isPending && "Deletando..."}
        </div>
      )}

      {(updateMutation.error || deleteMutation.error) && (
        <div className="mt-3 text-sm text-red-600 bg-red-50 p-2 rounded-lg">
          {updateMutation.error?.message || deleteMutation.error?.message}
        </div>
      )}
    </div>
  );
};
